<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({ open: { type: Boolean, default: false }, title: { type: String, default: 'Диалог' }, description: { type: String, default: '' }, size: { type: String, default: 'md' }, closeOnBackdrop: { type: Boolean, default: true } })
const emit = defineEmits(['request-close'])
const panel = ref(null)
let previousFocus = null
let previousOverflow = ''
const focusables = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
const sizeClasses = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }

function requestClose() { emit('request-close') }
function onKeydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); requestClose(); return }
  if (event.key !== 'Tab' || !panel.value) return
  const items = [...panel.value.querySelectorAll(focusables)].filter((el) => el instanceof HTMLElement && !el.hidden)
  if (!items.length) { event.preventDefault(); panel.value.focus(); return }
  const [first] = items
  const last = items[items.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}

watch(() => props.open, async (open) => {
  if (open) {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    await nextTick()
    const first = panel.value?.querySelector(focusables)
    if (first instanceof HTMLElement) first.focus(); else panel.value?.focus()
  } else {
    document.body.style.overflow = previousOverflow
    await nextTick()
    previousFocus?.focus?.()
  }
})
onBeforeUnmount(() => { document.body.style.overflow = previousOverflow })
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="open" class="fixed inset-0 z-[70] grid place-items-end bg-slate-950/50 p-0 backdrop-blur-[2px] sm:place-items-center sm:p-5" @keydown="onKeydown">
        <button class="absolute inset-0" type="button" tabindex="-1" aria-label="Закрыть окно" @click="closeOnBackdrop && requestClose()"></button>
        <section ref="panel" role="dialog" aria-modal="true" :aria-label="title" tabindex="-1" class="relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-[1.75rem] bg-white shadow-2xl outline-none sm:rounded-app-card" :class="sizeClasses[size] || sizeClasses.md">
          <header class="flex shrink-0 items-start justify-between gap-4 border-b border-app-border px-5 py-4 sm:px-6">
            <div><h2 class="text-lg font-black text-app-text">{{ title }}</h2><p v-if="description" class="mt-1 text-sm font-semibold text-app-muted">{{ description }}</p></div>
            <button type="button" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-app-control text-app-muted hover:bg-slate-100" aria-label="Закрыть" @click="requestClose">
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="m6 6 12 12M18 6 6 18"/></svg>
            </button>
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6"><slot /></div>
          <footer v-if="$slots.footer" class="shrink-0 border-t border-app-border bg-slate-50 px-5 py-4 sm:px-6"><slot name="footer" /></footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
